/* eslint-disable react/jsx-props-no-spreading */
import { Box, Typography, useScrollTrigger } from "@material-ui/core";
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { RouterProps } from "react-router";
import { useInView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";
import clsx from "clsx";
import {
  ActivitiesEntryTypeEnum,
  CallState,
  ExperienceId,
  ExperienceOptionId,
  VIEWED_ACTIVITY_DETAILS,
} from "redmond";
import queryStringParser from "query-string";
import {
  AccordionCollection,
  AccordionContent,
  ActionButton,
  ActionLink,
  CloseButtonIcon,
  Header,
  Icon,
  IconName,
  MobilePopoverCard,
  PageTabNavigations,
  PageTabNavigationsItem,
  useDeviceTypes,
} from "halifax";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  getExperiencesAvailabilityQuery,
  getExperiencesShopQuery,
  useIsTravelApp,
  useUpdateUserMetadataMutation,
} from "@capone/common";
import { PATH_AVAILABILITY, PATH_SHOP } from "../../utils/paths";

import { ClientContext } from "../../App";
import { ExperiencesShopConnectorProps } from "./container";
import "./styles.scss";
import { RewardsAccountSelection } from "../rewards/components";
import {
  BACK_TO_RESULTS,
  COMPARE_OTHER_SIMILAR_EXPERIENCES,
  CUSTOMER_REVIEWS,
  OTHER_SIMILAR_EXPERIENCES,
  SHOP_DETAIL_HEADER_TEXT,
  TRAVELERS_PHOTOS,
  WHAT_TO_EXPECT,
  WHATS_INCLUDED,
} from "./textConstants";
import { TravelWalletDrawer } from "../travel-wallet/components";

import { ShopPhotoGallery, PhotoGroup } from "./components/ShopPhotoGallery";
import { ShopOverview } from "./components/ShopOverview/component";
import { ShopWhatToExpect } from "./components/ShopWhatToExpect/component";
import { ShopCancellationPolicy } from "./components/ShopCancellationPolicy/component";
import { ShopWhatsIncluded } from "./components/ShopWhatsIncluded/component";
import { ShopCustomerReviews } from "./components/ShopCustomerReviews/component";
import { ShopCheckoutBreakdown } from "./components/ShopCheckoutBreakdown/component";
import { ShopSimilar } from "./components/ShopSimilar/component";
import { ExperiencesShopNavTab, SHOP_NAV_TABS } from "./constants";
import {
  scrollToSection,
  ShopContactInformation,
  EarnInfoBanner,
} from "../common";
import {
  CANCELLATION_POLICY,
  CONTACT_INFORMATION,
} from "../common/textConstants";
import { ShopMobileHeaderCard } from "./components/ShopMobileHeaderCard/component";
import { ShopMobileAvailabilityCard } from "./components/ShopMobileAvailabilityCard/component";
import { ShopErrorModal } from "./components/ShopErrorModal";
import { UnavailableDateModal } from "./components/UnavailableDateModal";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  COMBINED_SEARCH_EXPERIMENT,
  DATELESS_SEARCH_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { ConnectedExperiencesSearchControl } from "../search/components/ExperiencesSearchControl/container";
import { MobileCombinedSearch } from "../common/MobileCombinedSearch/component";
import { useResolveDestinationId } from "../../hooks/useResolveDestinationId";

// Setup dayjs plugins needed for timezone conversions
dayjs.extend(utc);
dayjs.extend(timezone);

const HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID =
  "activities-homepage-header-account-nav-slot";

export interface IExperiencesShop
  extends ExperiencesShopConnectorProps,
    RouterProps {}

export const ExperiencesShop = (props: IExperiencesShop) => {
  const {
    history,
    locationLabel,
    experienceName,
    experienceProviderName,
    shopOverview,
    isLoading,
    fetchExperiencesShop,
    shopWhatsIncluded,
    shopContactInfo,
    shopWhatToExpect,
    setFromDate,
    setUntilDate,
    fromDate,
    untilDate,
    shopGallery,
    fetchExperiencesShopReviews,
    selectedExperienceId,
    shopSimilar,
    largestValueAccount,
    fetchExperiencesShopOptions,
    shopCancellationPolicy,
    parsedAgeBands,
    setTravelerCounts,
    travelerCounts,
    shopCustomerReviews,
    shopPricingBreakdown,
    rewardsKey,
    fetchTravelWalletDetails,
    shopCallState,
    shopOptionsCallState,
    activityDetailsProperties,
    selectedTime,
    setSelectedTime,
    selectedDate,
    setSelectedDate,
    selectedBookableItem,
    setSelectedBookableItem,
    noAvailability,
    selectedRewardsAccount,
    entryType,
  } = props;

  const [updateUserMetadata] = useUpdateUserMetadataMutation();

  const { showCoreHomepage } = useContext(ClientContext);

  const { matchesMobile, matchesDesktop } = useDeviceTypes();

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isDatelessSearchEnabled =
    getExperimentVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const isCombinedSearchEnabled =
    getExperimentVariant(expState.experiments, COMBINED_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const { isTravelApp } = useIsTravelApp();

  const timeZone =
    shopOverview?.startingTimeZone || shopCancellationPolicy?.startingTimeZone;

  // Navigate back to availability, preserving category/attraction filters from shopContext
  const goBackToAvailability = useCallback(() => {
    let categoryTagId: string | null = null;
    try {
      const ctx = localStorage.getItem(
        `shop-context-${selectedExperienceId?.value}`
      );
      if (ctx) {
        const shopCtx = JSON.parse(ctx);
        if (shopCtx.entryTag?.id) {
          const kind =
            shopCtx.entryTag?.kind?.ActivityTagKind === "Attraction"
              ? "_1"
              : "_0";
          categoryTagId = `${shopCtx.entryTag.id}${kind}`;
        }
      }
    } catch {
      // Ignore parse errors
    }
    const baseQuery = `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
      locationLabel || "",
      fromDate,
      untilDate,
      undefined,
      isTravelApp
    )}`;
    const filterParam = categoryTagId ? `&categoryTagId=${categoryTagId}` : "";
    history.push(`${baseQuery}${filterParam}`);
  }, [
    history,
    locationLabel,
    fromDate,
    untilDate,
    isTravelApp,
    selectedExperienceId,
  ]);

  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [mobileShopSearchOpen, setMobileShopSearchOpen] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<
    ExperienceOptionId | undefined
  >(undefined);

  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });
  const [gallerySectionRef, gallerySectionInView] = useInView({
    threshold: 0.8,
  });
  const [overviewSectionRef, overviewSectionInView] = useInView({
    threshold: 0.8,
  });
  const [expectSectionRef, expectSectionInView] = useInView({ threshold: 0.8 });
  const [cancelSectionRef, cancelSectionInView] = useInView({ threshold: 0.8 });
  const [includedSectionRef, includedSectionInView] = useInView({
    threshold: 0.8,
  });
  const [reviewSectionRef, reviewSectionInView] = useInView({ threshold: 0.8 });
  const [travelerPhotoSectionRef, travelerPhotoSectionInView] = useInView({
    threshold: 0.8,
  });
  const [contactSectionRef, contactSectionInView] = useInView({
    threshold: 0.8,
  });
  const [compareSectionRef, compareSectionInView] = useInView({
    threshold: 0.8,
  });

  const selectedTabIndex = useMemo(() => {
    if (
      gallerySectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.GALLERY
        : true)
    ) {
      return 0;
    }
    if (
      overviewSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.OVERVIEW
        : true)
    ) {
      return 1;
    }
    if (
      expectSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.WHAT_TO_EXPECT
        : true)
    ) {
      return 2;
    }
    if (
      cancelSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.CANCELLATION_POLICY
        : true)
    ) {
      return 3;
    }

    if (
      includedSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.WHATS_INCLUDED
        : true)
    ) {
      return 4;
    }

    if (
      reviewSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.REVIEWS
        : true)
    ) {
      return 5;
    }
    if (
      travelerPhotoSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.TRAVELER_PHOTOS
        : true)
    ) {
      return 6;
    }
    if (
      contactSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.CONTACT
        : true)
    ) {
      return 7;
    }
    if (
      compareSectionInView &&
      (selectedSectionId
        ? selectedSectionId === ExperiencesShopNavTab.COMPARE
        : true)
    ) {
      return 8;
    }

    return undefined;
  }, [
    gallerySectionInView,
    overviewSectionInView,
    expectSectionInView,
    cancelSectionInView,
    includedSectionInView,
    reviewSectionInView,
    travelerPhotoSectionInView,
    contactSectionInView,
    compareSectionInView,
    selectedSectionId,
  ]);

  const tabs = SHOP_NAV_TABS.map((nav) => {
    // remove traveler photo tab if there's no traveler photos
    if (
      nav === ExperiencesShopNavTab.TRAVELER_PHOTOS &&
      shopGallery?.travelerPhotos &&
      shopGallery?.travelerPhotos?.length === 0
    ) {
      return undefined;
    }

    return {
      label: nav,
      onClick: () => {
        setSelectedSectionId(nav);
        // clearing out selectedSectionId to resume naturally updated nav tab selection as user scroll
        // 1 sec to allow scroll behavior to finish
        setTimeout(() => {
          setSelectedSectionId(undefined);
        }, 1000);
        scrollToSection(nav);
      },
    };
  }).filter((nav) => nav !== undefined) as PageTabNavigationsItem[];

  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );

  useEffect(() => {
    fetchTravelWalletDetails();
  }, []);

  useEffect(() => {
    fetchExperiencesShop({
      history,
      isDatelessEnabled: isDatelessSearchEnabled,
    });
  }, [queryString.activityId]);

  useEffect(() => {
    if (shopCallState === CallState.Success) {
      updateUserMetadata({
        userMetadata: {
          userHasVisitedActivityShopPage: true,
        },
      });
      const { properties, encryptedProperties } = activityDetailsProperties;
      trackEvent({
        eventName: VIEWED_ACTIVITY_DETAILS,
        properties,
        encryptedProperties,
      });
    }
  }, [shopCallState]);

  const shopSearchInitialText =
    experienceName && locationLabel
      ? `${experienceName} - ${locationLabel}`
      : experienceName || locationLabel || undefined;

  // Resolve the shop's destinationId up-front so combined search scopes
  // categories / attractions / products to *this* destination. Redux `location`
  // is owned by the availability saga and may be stale or absent on the shop
  // page (e.g. after navigating via "Similar Activities" or via deep link).
  const shopDestinationId = useResolveDestinationId(
    isCombinedSearchEnabled ? locationLabel || undefined : undefined
  );

  const renderDesktopHeaderView = () => (
    <Box className="experiences-shop-container">
      <Header
        className="rewards-components-section"
        left={
          <Box className="rewards-account-section-left-content">
            <ActionButton
              className="back-to-results"
              message={BACK_TO_RESULTS}
              onClick={goBackToAvailability}
              defaultStyle="h4r-secondary"
            />
            {isCombinedSearchEnabled ? (
              <Box className="shop-header-combined-search">
                <ConnectedExperiencesSearchControl
                  hideTripDestinations
                  combinedSearchPageContext="shop"
                  combinedSearchOnly
                  combinedSearchInitialText={shopSearchInitialText}
                  combinedSearchLocationLabel={locationLabel || undefined}
                  combinedSearchDestinationId={shopDestinationId}
                />
              </Box>
            ) : (
              <Box className="rewards-account-section-travel-details">
                <Typography variant="body1" tabIndex={0}>
                  {SHOP_DETAIL_HEADER_TEXT(
                    experienceName ?? "",
                    locationLabel ?? ""
                  )}
                </Typography>
                <Typography variant="body2" tabIndex={0}>
                  {experienceProviderName}
                </Typography>
              </Box>
            )}
          </Box>
        }
        right={
          showCoreHomepage ? (
            <Box
              className="desktop-experiences-shop-homepage-account-nav"
              id={HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID}
            />
          ) : (
            <Box className="desktop-experiences-shop-rewards-account-contents">
              <RewardsAccountSelection
                className={clsx("b2b hide-balance-border")}
                popoverClassName="b2b"
              />
              <TravelWalletDrawer />
            </Box>
          )
        }
      />
      <div
        ref={gallerySectionRef}
        id={ExperiencesShopNavTab.GALLERY}
        className="experiences-shop-gallery-container"
      >
        <ShopPhotoGallery
          isSkeleton={isLoading}
          {...shopGallery}
          priceBreakdown={shopPricingBreakdown?.priceBreakdown}
          parsedAgeBands={parsedAgeBands}
          largestValueAccount={largestValueAccount}
          activityDetailsProperties={activityDetailsProperties}
        />
      </div>

      <PageTabNavigations
        navItems={tabs}
        selectedTab={selectedTabIndex}
        className="experiences-shop-navigation"
      />

      <Box className="experiences-shop-split-view-container">
        <Box className="experiences-shop-split-view-section">
          <Box className="experiences-shop-left-section">
            <div
              ref={overviewSectionRef}
              id={ExperiencesShopNavTab.OVERVIEW}
              className="experiences-shop-section-container"
            >
              <Typography className="experiences-shop-component-title">
                {shopOverview?.title}
              </Typography>
              <ShopOverview
                selectedOptionId={selectedOptionId}
                setSelectedOptionId={setSelectedOptionId}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                fromDate={fromDate}
                untilDate={untilDate}
                isSkeleton={isLoading}
                {...shopOverview}
                setFromDate={setFromDate}
                setUntilDate={setUntilDate}
                fetchExperiencesShop={() => {
                  fetchExperiencesShop({
                    history,
                    isDatelessEnabled: isDatelessSearchEnabled,
                  });
                }}
                fetchExperiencesShopOptions={fetchExperiencesShopOptions}
                selectedBookableItem={selectedBookableItem}
                setSelectedBookableItem={setSelectedBookableItem}
                parsedAgeBands={parsedAgeBands}
                setTravelerCounts={setTravelerCounts}
                travelerCounts={travelerCounts}
                largestValueAccount={largestValueAccount}
                disableCheckout={
                  noAvailability || shopOptionsCallState !== CallState.Success
                }
                isDatelessSearch={isDatelessSearchEnabled}
              />
            </div>
            <div
              ref={expectSectionRef}
              id={ExperiencesShopNavTab.WHAT_TO_EXPECT}
              className="experiences-shop-section-container"
            >
              <Typography className="experiences-shop-component-title">
                {WHAT_TO_EXPECT}
              </Typography>
              <ShopWhatToExpect isSkeleton={isLoading} {...shopWhatToExpect} />
            </div>
            <div
              ref={cancelSectionRef}
              id={ExperiencesShopNavTab.CANCELLATION_POLICY}
              className="experiences-shop-section-container"
            >
              <Typography className="experiences-shop-component-title">
                {CANCELLATION_POLICY}
              </Typography>
              <ShopCancellationPolicy
                isSkeleton={isLoading}
                {...shopCancellationPolicy}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                startingTimeZone={timeZone}
              />
            </div>
            <div
              ref={includedSectionRef}
              id={ExperiencesShopNavTab.WHATS_INCLUDED}
              className="experiences-shop-section-container"
            >
              <Typography className="experiences-shop-component-title">
                {WHATS_INCLUDED}
              </Typography>
              <ShopWhatsIncluded
                isSkeleton={isLoading}
                {...shopWhatsIncluded}
              />
            </div>
            {(shopCustomerReviews?.summary?.numberOfReviews ?? 0) > 0 ? (
              <div
                ref={reviewSectionRef}
                id={ExperiencesShopNavTab.REVIEWS}
                className="experiences-shop-section-container"
              >
                <Typography className="experiences-shop-component-title">
                  {CUSTOMER_REVIEWS}
                </Typography>
                <ShopCustomerReviews
                  isSkeleton={isLoading}
                  {...shopCustomerReviews}
                  fetchExperiencesShopReviews={fetchExperiencesShopReviews}
                  selectedExperienceId={selectedExperienceId}
                />
              </div>
            ) : undefined}
            {shopGallery?.travelerPhotos &&
              shopGallery?.travelerPhotos?.length > 0 && (
                <div
                  ref={travelerPhotoSectionRef}
                  id={ExperiencesShopNavTab.TRAVELER_PHOTOS}
                  className="experiences-shop-section-container"
                >
                  <Typography className="experiences-shop-component-title">
                    {TRAVELERS_PHOTOS}{" "}
                    {shopGallery?.travelerPhotos?.length
                      ? ` (${shopGallery.travelerPhotos.length})`
                      : null}
                  </Typography>
                  <ShopPhotoGallery
                    {...shopGallery}
                    selectedGroup={PhotoGroup.TRAVELER}
                    priceBreakdown={shopPricingBreakdown?.priceBreakdown}
                    parsedAgeBands={parsedAgeBands}
                    largestValueAccount={largestValueAccount}
                    activityDetailsProperties={activityDetailsProperties}
                  />
                </div>
              )}
            <div
              ref={contactSectionRef}
              id={ExperiencesShopNavTab.CONTACT}
              className="experiences-shop-section-container"
            >
              <Typography className="experiences-shop-component-title">
                {CONTACT_INFORMATION}
              </Typography>
              <ShopContactInformation
                isSkeleton={isLoading}
                {...shopContactInfo}
              />
            </div>
          </Box>
          <Box className="experiences-shop-right-section">
            <ShopCheckoutBreakdown
              parsedAgeBands={parsedAgeBands}
              largestValueAccount={largestValueAccount}
              disableCheckout={
                noAvailability || shopOptionsCallState !== CallState.Success
              }
              {...shopPricingBreakdown}
              cancellationPolicyProps={{
                selectedDate,
                selectedTime,
                startingTimeZone: timeZone,
                isSkeleton: isLoading,
                cancellationPolicy: shopCancellationPolicy?.cancellationPolicy,
              }}
              noAvailability={noAvailability}
              isDatelessSearch={isDatelessSearchEnabled}
              selectionButtonsProps={
                isDatelessSearchEnabled
                  ? {
                      experienceId: selectedExperienceId,
                      selectedDate,
                      setSelectedDate,
                      selectedTime,
                      setSelectedTime,
                      timeSelectOptions: shopOverview?.timeSelectOptions,
                      travelerCounts,
                      setTravelerCounts,
                      parsedAgeBands,
                      experienceMaxTravelers:
                        shopOverview?.experienceMaxTravelers,
                      adultRequired: shopOverview?.adultRequired,
                      selectedBookableItem,
                      bookableItems: shopOverview?.bookableItems,
                      pricingCategory: shopOverview?.pricingCategory,
                      fromDate,
                      untilDate,
                      setFromDate,
                      setUntilDate,
                      fetchExperiencesShop: () => {
                        fetchExperiencesShop({
                          history,
                          isDatelessEnabled: isDatelessSearchEnabled,
                        });
                      },
                      fetchExperiencesShopOptions,
                      selectedOptionId,
                      pricingOpaque: shopOverview?.pricingOpaque,
                    }
                  : undefined
              }
            />
            <EarnInfoBanner
              largestValueAccount={largestValueAccount}
              priceBreakdown={shopPricingBreakdown?.priceBreakdown}
            />
          </Box>
        </Box>
        <div
          ref={compareSectionRef}
          id={ExperiencesShopNavTab.COMPARE}
          className="experiences-compare-section-container"
        >
          <Typography className="experiences-shop-component-title">
            {COMPARE_OTHER_SIMILAR_EXPERIENCES}
          </Typography>
          <ShopSimilar
            isSkeleton={isLoading}
            selectedRewardsAccount={selectedRewardsAccount}
            fromDate={fromDate}
            untilDate={untilDate}
            {...shopSimilar}
            rewardsKey={rewardsKey}
            disableCheckout={
              noAvailability || shopOptionsCallState !== CallState.Success
            }
            locationLabel={locationLabel}
            activityDetailsProperties={activityDetailsProperties}
            entryType={entryType}
            entryTypesAll={activityDetailsProperties.properties.entry_types_all}
          />
        </div>
      </Box>
    </Box>
  );

  const mobileAccordianCardTitle = (title: string) => (
    <Typography variant="h6">{title}</Typography>
  );

  const renderMobileView = () => (
    <Box className="experiences-shop-container-mobile">
      <Box
        className={clsx("experiences-shop-header-root global-mobile-nav", {
          scrolled: scrollTrigger,
          "travel-app": isTravelApp,
        })}
      >
        <Box className="shop-header-container">
          {!isTravelApp ? (
            <Box className="go-back-button-section">
              <ActionLink
                className="mobile-experiences-search-header-go-back"
                onClick={goBackToAvailability}
                content={<FontAwesomeIcon icon={faChevronLeft} />}
              />
            </Box>
          ) : undefined}
          <Box className="header-middle-section">
            {isCombinedSearchEnabled ? (
              <Box
                className="mobile-shop-combined-search-button"
                onClick={() => setMobileShopSearchOpen(true)}
                role="button"
                tabIndex={0}
              >
                <Box className="mobile-shop-search-text">
                  <Box className="mobile-shop-search-label">
                    Search by name or keyword
                  </Box>
                  <Box className="mobile-shop-search-value">
                    {experienceName || locationLabel || ""}
                  </Box>
                </Box>
                <Box className="mobile-shop-search-edit-icon">
                  <Icon name={IconName.B2BEditPencil} ariaLabel="Search" />
                </Box>
              </Box>
            ) : (
              <>
                {experienceName && (
                  <Typography
                    variant="body1"
                    tabIndex={0}
                    className="experiences-shop-mobile-header-title"
                  >
                    {SHOP_DETAIL_HEADER_TEXT(
                      experienceName,
                      locationLabel ?? ""
                    )}
                  </Typography>
                )}
                <Typography variant="body2" tabIndex={0}>
                  {experienceProviderName}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {isCombinedSearchEnabled && mobileShopSearchOpen && (
        <MobilePopoverCard
          open={mobileShopSearchOpen}
          className="mobile-shop-search-popup"
          onClose={() => setMobileShopSearchOpen(false)}
          headerElement={
            <Box className="modal-header-container">
              <Typography className="header-title">Search</Typography>
              <ActionLink
                className="modal-close-button"
                onClick={() => setMobileShopSearchOpen(false)}
                content={<CloseButtonIcon className="close-button-icon" />}
                label="Close"
                showTappableArea
              />
            </Box>
          }
          fullScreen
        >
          <MobileCombinedSearch
            pageContext="shop"
            destinationId={shopDestinationId}
            destinationName={locationLabel || undefined}
            fromDate={fromDate}
            untilDate={untilDate}
            showTrips={false}
            onClose={() => setMobileShopSearchOpen(false)}
          />
        </MobilePopoverCard>
      )}
      <ShopPhotoGallery
        isSkeleton={isLoading}
        {...shopGallery}
        mobile={matchesMobile}
        priceBreakdown={shopPricingBreakdown?.priceBreakdown}
        parsedAgeBands={parsedAgeBands}
        largestValueAccount={largestValueAccount}
        activityDetailsProperties={activityDetailsProperties}
      />
      <ShopMobileHeaderCard
        isSkeleton={isLoading}
        {...shopOverview}
        isFreeCancellation={shopPricingBreakdown?.isFreeCancellation}
        pricingBreakdown={shopPricingBreakdown?.priceBreakdown}
        parsedAgeBands={parsedAgeBands}
        largestValueAccount={largestValueAccount}
        cancellationPolicyProps={{
          selectedDate,
          selectedTime,
          startingTimeZone: timeZone,
          isSkeleton: isLoading,
          cancellationPolicy: shopCancellationPolicy?.cancellationPolicy,
        }}
      />
      <EarnInfoBanner
        mobile={matchesMobile}
        largestValueAccount={largestValueAccount}
        priceBreakdown={shopPricingBreakdown?.priceBreakdown}
      />
      <Box
        className={clsx("experiences-shop-mobile-availability-wrapper", {
          multivariant:
            shopOverview?.bookableItems &&
            shopOverview?.bookableItems.length > 1,
        })}
      >
        <ShopMobileAvailabilityCard
          selectedOptionId={selectedOptionId}
          setSelectedOptionId={setSelectedOptionId}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          fromDate={fromDate}
          untilDate={untilDate}
          isSkeleton={isLoading}
          {...shopOverview}
          setFromDate={setFromDate}
          setUntilDate={setUntilDate}
          fetchExperiencesShop={() => {
            fetchExperiencesShop({
              history,
              isDatelessEnabled: isDatelessSearchEnabled,
            });
          }}
          fetchExperiencesShopOptions={fetchExperiencesShopOptions}
          selectedBookableItem={selectedBookableItem}
          setSelectedBookableItem={setSelectedBookableItem}
          parsedAgeBands={parsedAgeBands}
          setTravelerCounts={setTravelerCounts}
          travelerCounts={travelerCounts}
          checkoutBreakdownProps={shopPricingBreakdown}
          disableCheckout={
            noAvailability || shopOptionsCallState !== CallState.Success
          }
          isDatelessSearch={isDatelessSearchEnabled}
          cancellationPolicyProps={{
            selectedDate,
            selectedTime,
            isSkeleton: isLoading,
            ...shopCancellationPolicy,
          }}
          noAvailability={noAvailability}
        />
        {shopOverview?.bookableItems &&
          shopOverview?.bookableItems.length <= 1 && (
            <ShopCheckoutBreakdown
              parsedAgeBands={parsedAgeBands}
              largestValueAccount={largestValueAccount}
              {...shopPricingBreakdown}
              cancellationPolicyProps={{
                selectedDate,
                selectedTime,
                isSkeleton: isLoading,
                ...shopCancellationPolicy,
              }}
              noAvailability={noAvailability}
              mobile
            />
          )}
      </Box>
      <AccordionCollection
        accordionContents={
          [
            {
              title: mobileAccordianCardTitle(ExperiencesShopNavTab.OVERVIEW),
              body: (
                <ShopOverview
                  selectedOptionId={selectedOptionId}
                  setSelectedOptionId={setSelectedOptionId}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  fromDate={fromDate}
                  untilDate={untilDate}
                  isSkeleton={isLoading}
                  {...shopOverview}
                  setFromDate={setFromDate}
                  setUntilDate={setUntilDate}
                  fetchExperiencesShop={() => {
                    fetchExperiencesShop({
                      history,
                      isDatelessEnabled: isDatelessSearchEnabled,
                    });
                  }}
                  fetchExperiencesShopOptions={fetchExperiencesShopOptions}
                  selectedBookableItem={selectedBookableItem}
                  setSelectedBookableItem={setSelectedBookableItem}
                  parsedAgeBands={parsedAgeBands}
                  setTravelerCounts={setTravelerCounts}
                  travelerCounts={travelerCounts}
                  mobile
                  largestValueAccount={largestValueAccount}
                  disableCheckout={
                    noAvailability || shopOptionsCallState !== CallState.Success
                  }
                />
              ),
            },
            {
              title: mobileAccordianCardTitle(
                ExperiencesShopNavTab.WHAT_TO_EXPECT
              ),
              body: (
                <ShopWhatToExpect
                  isSkeleton={isLoading}
                  {...shopWhatToExpect}
                  mobile
                />
              ),
            },
            {
              title: mobileAccordianCardTitle(
                ExperiencesShopNavTab.CANCELLATION_POLICY
              ),
              body: (
                <ShopCancellationPolicy
                  isSkeleton={isLoading}
                  {...shopCancellationPolicy}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                />
              ),
            },
            {
              title: mobileAccordianCardTitle(
                ExperiencesShopNavTab.WHATS_INCLUDED
              ),
              body: (
                <ShopWhatsIncluded
                  isSkeleton={isLoading}
                  {...shopWhatsIncluded}
                />
              ),
            },
            {
              title: mobileAccordianCardTitle(ExperiencesShopNavTab.REVIEWS),
              body: (
                <ShopCustomerReviews
                  isSkeleton={isLoading}
                  {...shopCustomerReviews}
                  fetchExperiencesShopReviews={fetchExperiencesShopReviews}
                  selectedExperienceId={selectedExperienceId}
                  mobile
                />
              ),
            },
            shopGallery?.travelerPhotos &&
              shopGallery?.travelerPhotos?.length > 0 && {
                title: mobileAccordianCardTitle(
                  ExperiencesShopNavTab.TRAVELER_PHOTOS
                ),
                body: (
                  <ShopPhotoGallery
                    {...shopGallery}
                    selectedGroup={PhotoGroup.TRAVELER}
                    mobile={matchesMobile}
                    priceBreakdown={shopPricingBreakdown?.priceBreakdown}
                    parsedAgeBands={parsedAgeBands}
                    largestValueAccount={largestValueAccount}
                    activityDetailsProperties={activityDetailsProperties}
                  />
                ),
              },
            {
              title: mobileAccordianCardTitle(ExperiencesShopNavTab.CONTACT),
              body: (
                <ShopContactInformation
                  isSkeleton={isLoading}
                  {...shopContactInfo}
                  mobile
                />
              ),
            },
          ].filter((item) => !!item) as AccordionContent[]
        }
        expandIcon={<Icon name="plus" />}
        collapseIcon={<Icon name="minus" />}
        className="experiences-shop-mobile-accordion"
      />
      <Box className="experiences-compare-section-container">
        <Typography
          variant="h6"
          className="experiences-compare-section-container-title"
        >
          {OTHER_SIMILAR_EXPERIENCES}
        </Typography>
        <ShopSimilar
          isSkeleton={isLoading}
          selectedRewardsAccount={selectedRewardsAccount}
          fromDate={fromDate}
          untilDate={untilDate}
          {...shopSimilar}
          mobile
          rewardsKey={rewardsKey}
          locationLabel={locationLabel}
          isTravelApp={isTravelApp}
          activityDetailsProperties={activityDetailsProperties}
          entryType={entryType}
          entryTypesAll={activityDetailsProperties.properties.entry_types_all}
          onClick={(experienceId: ExperienceId) => {
            history.push(
              `${PATH_SHOP}${getExperiencesShopQuery(
                experienceId,
                fromDate,
                untilDate,
                locationLabel,
                isTravelApp,
                undefined,
                ActivitiesEntryTypeEnum.SIMILAR_ACTIVITIES,
                activityDetailsProperties.properties.entry_types_all ??
                  (entryType ? [entryType] : [])
              )}`
            );
            window.location.reload();
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      className={clsx("experiences-shop-root", {
        "full-width": !matchesMobile,
        "travel-app": isTravelApp,
        xmod: isXmodRedesign,
        "core-homepage": showCoreHomepage,
      })}
    >
      <Box>
        {matchesDesktop && renderDesktopHeaderView()}
        {matchesMobile && renderMobileView()}
      </Box>
      <ShopErrorModal skipNoAvailability={isDatelessSearchEnabled} />
      {isDatelessSearchEnabled && <UnavailableDateModal />}
    </Box>
  );
};

export interface IShopComponentSkeletonProps {
  name: string;
}
export const ShopComponentSkeleton = ({
  name,
}: IShopComponentSkeletonProps) => (
  <Skeleton className={clsx("experiences-shop-skeleton", `${name}`)} />
);
